import React, { useEffect, useState } from "react"
import { Select } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { CustomTimeDropdown } from "../CustomTimeDropdown"

const { Option } = Select

const predefinedOptions = [
  { label: "1 hr", value: 60 },
  { label: "2 hr", value: 120 },
  { label: "3 hr", value: 180 },
  { label: "4 hr", value: 240 }
]

const DAY_OPTIONS = Array.from({ length: 29 }, (_, i) => ({ label: `${i + 2} days`, value: i + 2 }))

export const ExperienceDuration = ({ form, expandedRowData, value, onChange, isDayBased }) => {
  const [customHour, setCustomHour] = useState(0)
  const [customMinute, setCustomMinute] = useState(0)
  const [error, setError] = useState("")
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()

  // Durations already used by other booking options — exclude the current row's own duration
  const currentDuration = expandedRowData?.trip_duration
  const takenDurations = new Set(
    (form.getFieldValue("takenDurations") || []).filter((d) => d !== currentDuration)
  )

  // Sync custom hour/minute inputs when value changes from outside (e.g. form.setFieldsValue on edit)
  useEffect(() => {
    if (value == null) {
      setCustomHour(0)
      setCustomMinute(0)
    } else {
      const isPredefined = predefinedOptions.some((opt) => opt.value === value)
      if (!isPredefined && !Number.isNaN(value)) {
        setCustomHour(Math.floor(value / 60))
        setCustomMinute(value % 60)
      }
    }
  }, [value])
  useEffect(() => {
    if (error) setError("")
  }, [customHour, customMinute])

  const getDropdownRender = (menu) => (
    <CustomTimeDropdown
      menu={menu}
      customHour={customHour}
      customMinute={customMinute}
      error={error}
      setCustomHour={setCustomHour}
      setCustomMinute={setCustomMinute}
      handleCustomChange={handleCustomChange}
      label={t("global.enter_custom_duration")}
    />
  )

  const handleSelect = (val) => {
    onChange?.(val)
    setOpen(false)
  }

  const handleCustomChange = () => {
    const minuteValue = Number(customMinute) || 0

    if (minuteValue < 0 || minuteValue > 59) {
      setError(t("experiences.scheduling.errors.minute_limit"))
      return
    }
    const total = customHour * 60 + minuteValue

    if (takenDurations.has(total)) {
      setError(t("experiences.pricing.private.already_taken"))
      return
    }

    onChange?.(total)
    setError("")
    setOpen(false)
  }

  if (isDayBased) {
    return (
      <div>
        <Select
          style={{ width: "100%" }}
          placeholder="Select"
          value={value ?? undefined}
          onChange={(val) => onChange?.(val)}
        >
          {DAY_OPTIONS.map((opt) => {
            const isTaken = takenDurations.has(opt.value)
            return (
              <Option key={opt.value} value={opt.value} disabled={isTaken}>
                {isTaken ? `${opt.label} (${t("experiences.pricing.private.already_taken")})` : opt.label}
              </Option>
            )
          })}
        </Select>
      </div>
    )
  }

  return (
    <div>
      <Select
        open={open}
        onDropdownVisibleChange={(visible) => setOpen(visible)}
        style={{ width: "100%" }}
        placeholder="Select"
        value={
          predefinedOptions.find((opt) => opt.value === value)?.label ||
          (value ? `${Math.floor(value / 60)} hr ${value % 60} min` : undefined)
        }
        onSelect={handleSelect}
        dropdownRender={getDropdownRender}
      >
        {predefinedOptions.map((opt) => {
          const isTaken = takenDurations.has(opt.value)
          return (
            <Option key={opt.value} value={opt.value} disabled={isTaken}>
              {isTaken ? `${opt.label} (${t("experiences.pricing.private.already_taken")})` : opt.label}
            </Option>
          )
        })}
      </Select>
    </div>
  )
}

ExperienceDuration.propTypes = {
  form: PropTypes.shape({
    getFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  expandedRowData: PropTypes.object,
  value: PropTypes.number,
  onChange: PropTypes.func,
  isDayBased: PropTypes.bool,
}
